﻿import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useDropdownSourceContext } from "@ryde/redux";
import {
  ActionsEnum,
  companyIdSelector,
  IDropdownItemExt,
  innerItemEditStyles,
  ISelectListItem,
  ISelectListItemExt,
  Log,
  useHasPermissions,
  useSystemParams,
} from "@ryde/shared";

import {
  ColorIconButton,
  MultiSelectBoundInputByControl,
} from "../../../controls";
import { DropdownTemplate } from "../dropdown.template";
import {
  emptyUserRole,
  IUserRoleItem,
  IUserRoleItemForm,
  userRoleFormFieldNames,
} from "./table-user-roles.models";
import { useUserRoleItemResolver } from "./use-user-role-item-resolver";
import { buildRoleDropdownSelectPresentation } from "./user-role-dropdown-presentation";

export const UserRoleEdit: FC<IUserRoleItemForm> = ({
  value,
  onCancelOuter,
  setValueOuter,
  fieldName,
}) => {
  const { t } = useTranslation();
  const companyId = useRecoilValue(companyIdSelector);
  const { showOnlyCompanyRoles } = useSystemParams();
  const { getDropdownItems } = useDropdownSourceContext();
  const [roleSelectItems, setRoleSelectItems] = useState<ISelectListItem[]>([]);
  const [roleBoldValues, setRoleBoldValues] = useState<
    (number | string)[] | undefined
  >();
  const { hasPermissions: canViewAllRolesWithPermission } = useHasPermissions({
    entity: "system",
    action: ActionsEnum.AllowGlobalRolesInCompanyMode,
  });

  const { userRoleInnerFormValidationSchema } = useUserRoleItemResolver();
  const methods = useForm<Partial<IUserRoleItem>>({
    defaultValues: value || emptyUserRole,
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(userRoleInnerFormValidationSchema),
  });

  const {
    trigger,
    getValues,
    control,
    watch,
    formState: { isValid, touchedFields, dirtyFields },
  } = methods;

  const onSaveClick = async (): Promise<void> => {
    const validated = await trigger();

    if (!validated) {
      return;
    }

    const valueSnapshot = getValues();

    Log.debug(
      `Set value for userRole item target field:  ${fieldName}`,
      valueSnapshot,
    );
    setValueOuter?.(fieldName, valueSnapshot, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancelOuter?.();
  };

  const userRoleId = watch("roleId");
  const userRoleSiteIds = watch("siteIds");
  const userRoleSupplierIds = watch("supplierIds");
  const userRoleUnitIds = watch("unitIds");
  const userRoleRideTypeIds = watch("rideTypeIds");

  const customDropdownItemsFilter = useEvent(
    (item: IDropdownItemExt | ISelectListItemExt) => {
      if (item.value === userRoleId) {
        return true;
      }

      if (item.isNotManagerRole) {
        return false;
      }

      if (!item?.isActive) {
        return false;
      }

      const ownCompanyRaw =
        companyId !== null && companyId !== undefined ? Number(companyId) : NaN;

      if (Number.isNaN(ownCompanyRaw)) {
        return true;
      }

      const ownCompany = ownCompanyRaw;
      const isOwnCompanyRole = Boolean(
        item.companyId && Number(item.companyId) === ownCompany,
      );
      const isGlobalRole = !item.companyId;

      if (showOnlyCompanyRoles) {
        return canViewAllRolesWithPermission
          ? isGlobalRole || isOwnCompanyRole
          : isOwnCompanyRole;
      }

      return isGlobalRole || isOwnCompanyRole;
    },
  );

  useEffect(() => {
    const loadRoleSelectItems = async (): Promise<void> => {
      const items = (await getDropdownItems("role")) as IDropdownItemExt[];

      const { selectItems, boldValues } = buildRoleDropdownSelectPresentation({
        items: items ?? [],
        isItemIncluded: customDropdownItemsFilter,
      });

      setRoleSelectItems(selectItems);
      setRoleBoldValues(boldValues);
    };

    loadRoleSelectItems();
  }, [
    canViewAllRolesWithPermission,
    companyId,
    customDropdownItemsFilter,
    getDropdownItems,
    showOnlyCompanyRoles,
    userRoleId,
  ]);

  const roleDropdownFormAttributes = useMemo(
    () => ({
      selectItems: roleSelectItems,
      boldValues: roleBoldValues,
    }),
    [roleBoldValues, roleSelectItems],
  );

  useEffect(() => {
    for (const dependantFieldName of userRoleFormFieldNames) {
      Log.debug(
        "Trigger dependant controls validation for ",
        dependantFieldName,
        userRoleId,
        userRoleSiteIds,
        userRoleSupplierIds,
        userRoleUnitIds,
        userRoleRideTypeIds,
      );

      trigger(dependantFieldName);
    }
  }, [
    trigger,
    userRoleSiteIds,
    userRoleSupplierIds,
    touchedFields,
    userRoleUnitIds,
    userRoleRideTypeIds,
    dirtyFields,
    userRoleId,
  ]);

  return (
    <FormProvider {...methods}>
      <div className="userRoles-item no-blur-item" style={innerItemEditStyles}>
        <div className="userRoles-item__editors">
          <DropdownTemplate
            name="roleId"
            formLabel={t("role.plural")}
            formValidation={{
              type: "number",
              rules: [{ validation: "required" }],
            }}
            formAttributes={roleDropdownFormAttributes}
            size="medium"
          />
          <MultiSelectBoundInputByControl
            name="siteIds"
            control={control}
            formLabel={t("site.plural")}
            formAttributes={{ dropItemsSource: "site" }}
          />
          <MultiSelectBoundInputByControl
            name="supplierIds"
            control={control}
            formLabel={t("supplier.plural")}
            formAttributes={{ dropItemsSource: "supplier" }}
          />
          <MultiSelectBoundInputByControl
            name="unitIds"
            control={control}
            formLabel={t("unit.plural")}
            formAttributes={{ dropItemsSource: "unit" }}
          />
          <MultiSelectBoundInputByControl
            name="rideTypeIds"
            control={control}
            formLabel={t("rideType.plural")}
            formAttributes={{ dropItemsSource: "rideType" }}
          />
        </div>
        <div className="userRoles-item__buttons">
          <ColorIconButton
            disabled={!isValid}
            iconType="CHECK"
            edge="end"
            aria-label="save"
            onClick={onSaveClick}
          />
          <ColorIconButton
            iconType="CANCEL"
            edge="end"
            aria-label="cancel"
            onClick={() => onCancelOuter?.()}
          />
        </div>
      </div>
    </FormProvider>
  );
};
